import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaEdit, FaExclamationTriangle,
  FaDownload, FaExpand, FaCompress, FaRedo,
} from 'react-icons/fa';
import {
  MdInsertDriveFile, MdArticle, MdTableChart,
  MdSlideshow, MdPictureAsPdf,
} from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { downloadAsset } from 'redux/drive/driveSlice';
import apiAxios from 'helpers/apiAxios';

const DOCUMENT_OFFICE_URL = process.env.REACT_APP_DOCUMENT_OFFICE_URL;
const DOCS_API_SRC = `${DOCUMENT_OFFICE_URL}/web-apps/apps/api/documents/api.js`;

// ── Load DocsAPI script ───────────────────────────────────────────────────────
const loadDocsApiScript = () =>
  new Promise((resolve, reject) => {
    if (window.DocsAPI) { resolve(); return; }
    const existing = document.querySelector(`script[src="${DOCS_API_SRC}"]`);
    if (existing) {
      if (window.DocsAPI) resolve();
      else {
        existing.addEventListener('load', resolve);
        existing.addEventListener('error', () =>
          reject(new Error(`Failed to load OnlyOffice API from ${DOCUMENT_OFFICE_URL}`))
        );
      }
      return;
    }
    const s = document.createElement('script');
    s.src     = DOCS_API_SRC;
    s.onload  = resolve;
    s.onerror = () => reject(new Error(`Failed to load OnlyOffice API from ${DOCUMENT_OFFICE_URL}`));
    document.head.appendChild(s);
  });

// ── Resolve config: location.state → sessionStorage fallback ─────────────────
const resolveState = (location) => {
  if (location?.state?.config) return location.state;
  const ck = new URLSearchParams(location?.search).get('ck');
  if (ck) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(ck) || 'null');
      if (saved) { sessionStorage.removeItem(ck); return { ...saved, configEndpoint: null }; }
    } catch { /* ignore */ }
  }
  return {};
};

// ── File type → icon + color ──────────────────────────────────────────────────
const fileTypeInfo = (contentType = '', name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  if (/word|doc/.test(contentType) || ['doc','docx','rtf','txt'].includes(ext))
    return { Icon: MdArticle,    color: '#3b82f6', label: 'Word' };
  if (/excel|spreadsheet|csv/.test(contentType) || ['xls','xlsx','csv'].includes(ext))
    return { Icon: MdTableChart, color: '#22c55e', label: 'Spreadsheet' };
  if (/powerpoint|presentation/.test(contentType) || ['ppt','pptx'].includes(ext))
    return { Icon: MdSlideshow,  color: '#f97316', label: 'Presentation' };
  if (/pdf/.test(contentType) || ext === 'pdf')
    return { Icon: MdPictureAsPdf, color: '#ef4444', label: 'PDF' };
  return { Icon: MdInsertDriveFile, color: '#6366f1', label: 'Document' };
};

// ── Tooltip button ────────────────────────────────────────────────────────────
const IconBtn = ({ onClick, title, disabled, children, active = false, variant = 'default' }) => {
  const base = 'tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-cursor-pointer tw-transition-all tw-border-0 tw-flex-shrink-0 tw-relative tw-group/btn';
  const styles = {
    default: `tw-bg-white tw-border tw-border-slate-200 tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 ${active ? 'tw-bg-slate-100 tw-text-slate-700' : ''}`,
    primary: 'tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700',
    danger:  'tw-bg-white tw-border tw-border-slate-200 tw-text-red-500 hover:tw-bg-red-50',
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={`${base} ${styles[variant]} disabled:tw-opacity-40 disabled:tw-cursor-not-allowed`}>
      {children}
    </button>
  );
};

IconBtn.propTypes = {
  onClick:  PropTypes.func,
  title:    PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  active:   PropTypes.bool,
  variant:  PropTypes.oneOf(['default', 'primary', 'danger']),
};

// ── DriveOnlyOfficeEditor ─────────────────────────────────────────────────────
const DriveOnlyOfficeEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const editorRef    = useRef(null);
  const containerRef = useRef(null);

  const [loadState,         setLoadState]         = useState('loading');
  const [errorMessage,      setErrorMessage]       = useState('');
  const [isSwitchingToEdit, setIsSwitchingToEdit]  = useState(false);
  const [isFullscreen,      setIsFullscreen]       = useState(false);
  const [elapsedSec,        setElapsedSec]         = useState(0);

  const { config: initialConfig, asset, configEndpoint } = resolveState(location);
  const { allow_download } = asset?.permissions || {};

  const [activeConfig, setActiveConfig] = useState(initialConfig);
  const [reinitKey,    setReinitKey]    = useState(0);

  const allowEdit  = activeConfig?.allow_edit ?? false;
  const isViewMode = (activeConfig?.editorConfig?.mode || 'view') === 'view';

  const contentType = activeConfig?.document?.fileType || '';
  const { Icon: FileIcon, color: fileColor, label: fileLabel } =
    fileTypeInfo(contentType, asset?.name || '');

  // ── Fullscreen ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // ── Loading elapsed timer ───────────────────────────────────────────────────
  useEffect(() => {
    if (loadState !== 'loading') { setElapsedSec(0); return; }
    const t = setInterval(() => setElapsedSec(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [loadState]);

  // ── Initialize editor ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeConfig || !DOCUMENT_OFFICE_URL) {
      setLoadState('error');
      setErrorMessage('Missing editor configuration. Please go back and try again.');
      return;
    }

    let cancelled = false;

    if (editorRef.current?.destroyEditor) {
      editorRef.current.destroyEditor();
      editorRef.current = null;
    }

    setLoadState('loading');

    loadDocsApiScript()
      .then(() => {
        if (cancelled || editorRef.current) return;
        editorRef.current = new window.DocsAPI.DocEditor('onlyoffice-editor', {
          ...activeConfig,
          width:  '100%',
          height: '100%',
          events: {
            onError: (e) => {
              if (cancelled) return;
              setErrorMessage(e?.data?.errorDescription || 'An error occurred in the editor.');
              setLoadState('error');
            },
            onAppReady: () => { if (!cancelled) setLoadState('ready'); },
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorMessage(err.message || 'Could not connect to OnlyOffice Document Server.');
          setLoadState('error');
        }
      });

    return () => {
      cancelled = true;
      if (editorRef.current?.destroyEditor) {
        editorRef.current.destroyEditor();
        editorRef.current = null;
      }
    };
  }, [activeConfig, reinitKey]);

  const handleSwitchToEdit = async () => {
    if (!configEndpoint) return;
    setIsSwitchingToEdit(true);
    try {
      const res = await apiAxios.get(configEndpoint);
      setActiveConfig(res.data);
      setReinitKey(k => k + 1);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to switch to edit mode.');
      setLoadState('error');
    } finally {
      setIsSwitchingToEdit(false);
    }
  };

  const handleDownload = () => {
    if (asset?.url)  window.open(asset.url, '_blank');
    else if (asset?.id) dispatch(downloadAsset({ assetId: asset.id, fileName: asset.name }));
  };

  const handleRetry = () => {
    setErrorMessage('');
    setReinitKey(k => k + 1);
  };

  return (
    <div
      ref={containerRef}
      className="tw-flex tw-flex-col tw-bg-[#f0f2f5]"
      style={{ height: isFullscreen ? '100vh' : 'max(320px, calc(100dvh - 56px))' }}
    >
      {/* ── Header ── */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-white tw-border-b tw-border-slate-200 tw-shadow-sm tw-flex-shrink-0">

        {/* Back */}
        <IconBtn onClick={() => navigate(-1)} title="Back">
          <FaArrowLeft size={12} />
        </IconBtn>

        {/* Divider */}
        <span className="tw-w-px tw-h-5 tw-bg-slate-200 tw-shrink-0" />

        {/* File info */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
          <div className="tw-w-7 tw-h-7 tw-rounded-sm tw-flex tw-items-center tw-justify-center tw-shrink-0"
            style={{ backgroundColor: `${fileColor}18` }}>
            <FileIcon size={16} style={{ color: fileColor }} />
          </div>
          <div className="tw-flex-1 tw-min-w-0">
            <p className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-mb-0 tw-truncate tw-leading-tight">
              {asset?.name || 'Document'}
            </p>
            <p className="tw-text-[10px] tw-text-slate-400 tw-mb-0 tw-leading-none">{fileLabel}</p>
          </div>

          {/* Mode badge */}
          <span className={`tw-flex-shrink-0 tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-xl tw-uppercase tw-tracking-wide ${
            isViewMode
              ? 'tw-bg-sky-50 tw-text-sky-600 tw-border tw-border-sky-100'
              : 'tw-bg-emerald-50 tw-text-emerald-600 tw-border tw-border-emerald-100'
          }`}>
            {isViewMode ? 'Viewing' : 'Editing'}
          </span>
        </div>

        {/* Action buttons */}
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">

          {/* Edit Document */}
          {allowEdit && isViewMode && (
            <button type="button" onClick={handleSwitchToEdit} disabled={isSwitchingToEdit}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-semibold tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 disabled:tw-opacity-50 tw-cursor-pointer disabled:tw-cursor-not-allowed tw-transition-colors tw-border-0">
              {isSwitchingToEdit
                ? <span className="tw-w-3 tw-h-3 tw-rounded-xl tw-border-2 tw-border-white/40 tw-border-t-white tw-animate-spin" />
                : <FaEdit size={10} />
              }
              Edit
            </button>
          )}

          {/* Download */}
          {allow_download && (
            <IconBtn onClick={handleDownload} title="Download file">
              <FaDownload size={12} />
            </IconBtn>
          )}

          {/* Divider */}
          <span className="tw-w-px tw-h-5 tw-bg-slate-200 tw-shrink-0" />

          {/* Fullscreen */}
          <IconBtn onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'} active={isFullscreen}>
            {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
          </IconBtn>
        </div>
      </div>

      {/* ── Editor area ── */}
      <div className="tw-flex-1 tw-min-h-0 tw-relative">
        {/*
          IMPORTANT: #onlyoffice-editor MUST render first.
          OnlyOffice replaces this div with an iframe; React must not use it as
          an insertBefore anchor for sibling overlays after the swap.
        */}
        <div id="onlyoffice-editor" className="tw-w-full tw-h-full" />

        {/* ── Loading overlay ── */}
        {loadState === 'loading' && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-5 tw-bg-[#f0f2f5] tw-z-10">
            {/* Animated file card */}
            <div className="tw-relative tw-flex tw-items-center tw-justify-center">
              <div className="tw-w-20 tw-h-20 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-bg-white">
                <FileIcon size={36} style={{ color: fileColor }} />
              </div>
              {/* Spinning ring */}
              <div className="tw-absolute tw-w-28 tw-h-28 tw-rounded-xl tw-border-4 tw-border-transparent tw-border-t-primary-500 tw-border-r-primary-200 tw-animate-spin" />
            </div>

            <div className="tw-text-center">
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-1">Opening document…</p>
              <p className="tw-text-xs tw-text-slate-400 tw-mb-0">
                {elapsedSec < 5
                  ? 'Connecting to document server'
                  : elapsedSec < 12
                  ? 'Loading document content'
                  : 'Almost ready…'}
              </p>
            </div>

            {/* Progress dots */}
            <div className="tw-flex tw-gap-1.5">
              {[0, 1, 2].map(i => (
                <span key={i} className="tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-bg-primary-400 tw-animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Error state ── */}
        {loadState === 'error' && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-5 tw-bg-[#f0f2f5] tw-z-10 tw-px-6">
            <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-p-8 tw-flex tw-flex-col tw-items-center tw-gap-4 tw-max-w-sm tw-w-full tw-text-center">
              <div className="tw-w-16 tw-h-16 tw-rounded-xl tw-bg-red-50 tw-border tw-border-red-100 tw-flex tw-items-center tw-justify-center">
                <FaExclamationTriangle size={24} className="tw-text-red-400" />
              </div>
              <div>
                <h6 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-1">Could not open document</h6>
                <p className="tw-text-sm tw-text-slate-500 tw-mb-0">{errorMessage}</p>
              </div>
              <div className="tw-flex tw-gap-2 tw-w-full">
                <button type="button" onClick={handleRetry}
                  className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded tw-text-xs tw-font-semibold tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors">
                  <FaRedo size={10} /> Retry
                </button>
                <button type="button" onClick={() => navigate(-1)}
                  className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded tw-text-xs tw-font-semibold tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-cursor-pointer tw-transition-colors tw-border-0">
                  <FaArrowLeft size={10} /> Go back
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Status bar ── */}
      {loadState === 'ready' && (
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-1 tw-bg-white tw-border-t tw-border-slate-100 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-bg-emerald-400" />
            <span className="tw-text-[10px] tw-text-slate-400 tw-font-medium">Document server connected</span>
          </div>
          <span className="tw-text-[10px] tw-text-slate-300">
            {isFullscreen ? 'Press Esc to exit fullscreen' : 'F — fullscreen'}
          </span>
        </div>
      )}
    </div>
  );
};

export default DriveOnlyOfficeEditor;
